import React, { useEffect, useState, useRef } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { MessageCircleIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, SearchIcon, FilterIcon, ChevronDownIcon, UserIcon, ClockIcon, PlusIcon, RefreshCwIcon, SendIcon, PaperclipIcon, TrashIcon, MoreHorizontalIcon, MailIcon, PhoneIcon, InfoIcon, ArrowRightIcon, ArrowLeftIcon, CalendarIcon, BuildingIcon, TagIcon, EyeIcon, Edit2Icon, ArrowUpIcon, ArrowDownIcon, SmileIcon, ImageIcon, FileTextIcon, HelpCircleIcon, MessageSquareIcon } from 'lucide-react';
// Mock data for support tickets
const generateMockTickets = (count: number, mdaId?: string) => {
  const statuses = ['Open', 'In Progress', 'Resolved', 'Closed', 'Pending'];
  const priorities = ['Low', 'Medium', 'High', 'Urgent'];
  const categories = ['Technical', 'Account', 'Payment', 'Application', 'General'];
  const mdas = ['Ministry of Finance', 'Ministry of Lands and Surveys', 'Ministry of Commerce and Industry', 'Federal Road Safety Commission', 'Ministry of Education'];
  const mockData = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const priority = priorities[Math.floor(Math.random() * priorities.length)];
    const category = categories[Math.floor(Math.random() * categories.length)];
    const mda = mdas[Math.floor(Math.random() * mdas.length)];
    const daysAgo = Math.floor(Math.random() * 30);
    const date = new Date(now);
    date.setDate(date.getDate() - daysAgo);
    let subject = '';
    let description = '';
    // Generate subject and description based on category
    if (category === 'Technical') {
      subject = ['Cannot access my dashboard', 'Error uploading documents', 'Page not loading correctly', 'System is very slow', 'Cannot submit application form'][Math.floor(Math.random() * 5)];
      description = ['I am unable to access my dashboard after logging in. The page keeps refreshing.', 'When I try to upload my documents, I get an error message saying "File type not supported".', 'The application form page is not loading correctly. Some fields are missing.', 'The system is extremely slow when I try to navigate between pages.', 'I filled out the application form but when I click submit, nothing happens.'][Math.floor(Math.random() * 5)];
    } else if (category === 'Account') {
      subject = ['Cannot reset my password', 'Need to update my profile information', 'Account verification issue', 'Unable to login', 'Need to change my email address'][Math.floor(Math.random() * 5)];
      description = ['I requested a password reset but did not receive any email.', 'I need to update my phone number and address in my profile but cannot find where to do it.', 'I submitted my verification documents 5 days ago but my account is still not verified.', 'When I try to login, it says my credentials are incorrect, but I am sure they are right.', 'I need to change the email address associated with my account.'][Math.floor(Math.random() * 5)];
    } else if (category === 'Payment') {
      subject = ['Payment failed but money deducted', 'Cannot see payment receipt', 'Double payment for same service', 'Payment showing as pending', 'Need refund for unused service'][Math.floor(Math.random() * 5)];
      description = ['I made a payment for my application but it failed. However, the money was deducted from my account.', 'I cannot find the receipt for the payment I made yesterday.', 'I was charged twice for the same service. I need a refund for one of the payments.', 'My payment has been showing as pending for 3 days now.', 'I paid for a service I did not use. I would like to request a refund.'][Math.floor(Math.random() * 5)];
    } else if (category === 'Application') {
      subject = ['Application status not updating', 'Need to modify submitted application', 'Application rejected without reason', 'Application processing taking too long', 'Missing documents for application'][Math.floor(Math.random() * 5)];
      description = ['I submitted my application 10 days ago but the status is still showing as "Submitted".', 'I need to modify some information on my already submitted application.', 'My application was rejected without any explanation. I need to know why.', 'It has been over a month since I submitted my application. How much longer will it take?', 'I need to add some documents to my submitted application.'][Math.floor(Math.random() * 5)];
    } else {
      subject = ['General inquiry about services', 'Need assistance with process', 'Information request', 'Feedback on platform', 'Suggestion for improvement'][Math.floor(Math.random() * 5)];
      description = ['I would like to know more about the services offered by this platform.', 'I need assistance with understanding the application process.', 'I am requesting information about the required documents for a service.', 'I want to provide feedback on my experience using the platform.', 'I have a suggestion for improving the user interface of the platform.'][Math.floor(Math.random() * 5)];
    }
    // Only include tickets for the specified MDA if mdaId is provided
    if (!mdaId || mda === mdaId) {
      mockData.push({
        id: `ticket-${i}-${Date.now()}`,
        subject,
        description,
        status,
        priority,
        category,
        mda,
        date: date.toISOString(),
        user: {
          id: `user-${Math.floor(Math.random() * 1000)}`,
          name: ['John Doe', 'Jane Smith', 'Michael Johnson', 'Sarah Williams', 'Robert Brown', 'Emily Davis', 'David Wilson', 'Jennifer Taylor', 'James Anderson', 'Lisa Thomas'][Math.floor(Math.random() * 10)],
          email: ['john.doe@example.com', 'jane.smith@example.com', 'michael.johnson@example.com', 'sarah.williams@example.com', 'robert.brown@example.com', 'emily.davis@example.com', 'david.wilson@example.com', 'jennifer.taylor@example.com', 'james.anderson@example.com', 'lisa.thomas@example.com'][Math.floor(Math.random() * 10)],
          phone: `+234 ${Math.floor(Math.random() * 1000)} ${Math.floor(Math.random() * 1000)} ${Math.floor(Math.random() * 10000)}`
        },
        assignedTo: Math.random() > 0.3 ? {
          id: `admin-${Math.floor(Math.random() * 100)}`,
          name: ['Admin User', 'Support Agent', 'Technical Support', 'Customer Service', 'Account Manager'][Math.floor(Math.random() * 5)]
        } : null,
        messages: generateMockMessages(Math.floor(Math.random() * 5) + 1, date),
        lastUpdated: new Date(date.getTime() + Math.floor(Math.random() * 86400000)).toISOString()
      });
    }
  }
  return mockData;
};
// Generate mock messages for a ticket
const generateMockMessages = (count: number, startDate: Date) => {
  const messages = [];
  let currentDate = new Date(startDate);
  for (let i = 0; i < count; i++) {
    // Add random hours to the date
    currentDate = new Date(currentDate.getTime() + Math.floor(Math.random() * 86400000));
    const isUser = i % 2 === 0;
    let content = '';
    if (isUser) {
      content = ['I am having an issue with the system. Can you please help?', 'Thank you for your response. I will try that solution.', 'The issue is still not resolved. Can you provide more assistance?', 'I have another question regarding my application.', 'I appreciate your help with this matter.'][Math.floor(Math.random() * 5)];
    } else {
      content = ['Thank you for contacting support. I will be happy to assist you with this issue.', 'Could you please provide more details about the problem you are experiencing?', 'I have checked your account and everything seems to be in order.', 'Please try clearing your browser cache and cookies, then try again.', 'Your issue has been escalated to our technical team. They will contact you shortly.'][Math.floor(Math.random() * 5)];
    }
    messages.push({
      id: `msg-${i}-${Date.now()}`,
      content,
      date: currentDate.toISOString(),
      isUser,
      sender: isUser ? 'User' : 'Support Agent'
    });
  }
  return messages;
};
// Format relative time
const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffInSeconds < 60) {
    return 'Just now';
  }
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  }
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  }
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric'
  };
  return date.toLocaleDateString(undefined, options);
};
const Support: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [tickets, setTickets] = useState<any[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<any[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [newMessage, setNewMessage] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [ticketStats, setTicketStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    averageResponseTime: '0h',
    resolutionRate: '0%'
  });
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    category: 'Technical',
    priority: 'Medium',
    mda: '',
    user: {
      name: '',
      email: '',
      phone: ''
    }
  });
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [selectedTicketId, setSelectedTicketId] = useState('');
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState('');
  const messageEndRef = useRef<HTMLDivElement>(null);
  const {
    hasPermission,
    isSuperAdmin,
    getMdaId,
    getMdaName
  } = useAdminAuth();
  const canManageTickets = hasPermission('support:manage');
  const canAssignTickets = hasPermission('support:assign');
  const canDeleteTickets = hasPermission('support:delete');
  // Load tickets
  useEffect(() => {
    setIsLoading(true);
    // Simulate API call delay
    setTimeout(() => {
      let allTickets = [];
      // If super admin, load all tickets
      // If MDA admin, only load tickets for their MDA
      if (isSuperAdmin()) {
        allTickets = generateMockTickets(50);
      } else {
        const mdaName = getMdaName();
        allTickets = generateMockTickets(20, mdaName);
      }
      setTickets(allTickets);
      setFilteredTickets(allTickets);
      // Calculate ticket stats
      const openTickets = allTickets.filter((t: any) => t.status === 'Open').length;
      const inProgressTickets = allTickets.filter((t: any) => t.status === 'In Progress').length;
      const resolvedTickets = allTickets.filter((t: any) => t.status === 'Resolved').length;
      setTicketStats({
        total: allTickets.length,
        open: openTickets,
        inProgress: inProgressTickets,
        resolved: resolvedTickets,
        averageResponseTime: '4.2h',
        resolutionRate: '87%'
      });
      setIsLoading(false);
    }, 1000);
  }, [isSuperAdmin, getMdaId, getMdaName]);
  // Apply filters
  useEffect(() => {
    let filtered = [...tickets];
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }
    // Apply priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(t => t.priority === priorityFilter);
    }
    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }
    // Apply active filter
    if (activeFilter === 'assigned') {
      filtered = filtered.filter(t => t.assignedTo !== null);
    } else if (activeFilter === 'unassigned') {
      filtered = filtered.filter(t => t.assignedTo === null);
    } else if (activeFilter === 'urgent') {
      filtered = filtered.filter(t => t.priority === 'Urgent');
    }
    // Apply search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => t.subject.toLowerCase().includes(query) || t.description.toLowerCase().includes(query) || t.user.name.toLowerCase().includes(query) || t.user.email.toLowerCase().includes(query) || t.mda.toLowerCase().includes(query));
    }
    // Apply sort order
    filtered.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    setFilteredTickets(filtered);
  }, [tickets, activeFilter, searchQuery, statusFilter, priorityFilter, categoryFilter, sortOrder]);
  // Scroll to bottom of messages when a ticket is selected or a new message is added
  useEffect(() => {
    if (selectedTicket && messageEndRef.current) {
      messageEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [selectedTicket, tickets]);
  // Toggle sidebar
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Toggle dropdown
  const toggleDropdown = (dropdown: string) => {
    if (activeDropdown === dropdown) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdown);
    }
  };
  // Handle ticket selection
  const selectTicket = (ticketId: string) => {
    setSelectedTicket(ticketId);
  };
  // Send a new message
  const sendMessage = () => {
    if (newMessage.trim() === '' || !selectedTicket) return;
    setIsReplying(true);
    // Simulate API call delay
    setTimeout(() => {
      const updatedTickets = tickets.map(ticket => {
        if (ticket.id === selectedTicket) {
          const now = new Date();
          // Add support agent message
          const updatedMessages = [...ticket.messages, {
            id: `msg-new-${Date.now()}`,
            content: newMessage,
            date: now.toISOString(),
            isUser: false,
            sender: 'Support Agent'
          }];
          // Update ticket status if it was open
          const updatedStatus = ticket.status === 'Open' ? 'In Progress' : ticket.status;
          return {
            ...ticket,
            messages: updatedMessages,
            status: updatedStatus,
            lastUpdated: now.toISOString()
          };
        }
        return ticket;
      });
      setTickets(updatedTickets);
      setNewMessage('');
      setIsReplying(false);
    }, 500);
  };
  // Update ticket status
  const updateTicketStatus = (ticketId: string, newStatus: string) => {
    const updatedTickets = tickets.map(ticket => {
      if (ticket.id === ticketId) {
        return {
          ...ticket,
          status: newStatus,
          lastUpdated: new Date().toISOString()
        };
      }
      return ticket;
    });
    setTickets(updatedTickets);
  };
  // Create a new ticket
  const createTicket = () => {
    if (newTicket.subject.trim() === '' || newTicket.description.trim() === '' || newTicket.user.name.trim() === '' || newTicket.user.email.trim() === '') {
      return;
    }
    setIsSubmittingTicket(true);
    // Simulate API call delay
    setTimeout(() => {
      const now = new Date();
      const newTicketObj = {
        id: `ticket-new-${Date.now()}`,
        subject: newTicket.subject,
        description: newTicket.description,
        status: 'Open',
        priority: newTicket.priority,
        category: newTicket.category,
        mda: isSuperAdmin() ? newTicket.mda : getMdaName() || '',
        date: now.toISOString(),
        user: {
          id: `user-new-${Date.now()}`,
          name: newTicket.user.name,
          email: newTicket.user.email,
          phone: newTicket.user.phone
        },
        assignedTo: null,
        messages: [{
          id: `msg-new-${Date.now()}`,
          content: newTicket.description,
          date: now.toISOString(),
          isUser: true,
          sender: 'User'
        }],
        lastUpdated: now.toISOString()
      };
      setTickets([newTicketObj, ...tickets]);
      setShowNewTicketModal(false);
      setIsSubmittingTicket(false);
      setNewTicket({
        subject: '',
        description: '',
        category: 'Technical',
        priority: 'Medium',
        mda: '',
        user: {
          name: '',
          email: '',
          phone: ''
        }
      });
    }, 1000);
  };
  // Assign ticket to agent
  const assignTicket = () => {
    if (selectedAssignee.trim() === '' || selectedTicketId === '') {
      return;
    }
    setIsAssigning(true);
    // Simulate API call delay
    setTimeout(() => {
      const updatedTickets = tickets.map(ticket => {
        if (ticket.id === selectedTicketId) {
          return {
            ...ticket,
            assignedTo: {
              id: `admin-${Date.now()}`,
              name: selectedAssignee
            },
            lastUpdated: new Date().toISOString()
          };
        }
        return ticket;
      });
      setTickets(updatedTickets);
      setShowAssignModal(false);
      setIsAssigning(false);
      setSelectedAssignee('');
      setSelectedTicketId('');
    }, 500);
  };
  // Delete ticket
  const deleteTicket = (ticketId: string) => {
    setTicketToDelete(ticketId);
    setShowDeleteConfirm(true);
  };
  // Confirm delete ticket
  const confirmDeleteTicket = () => {
    const updatedTickets = tickets.filter(ticket => ticket.id !== ticketToDelete);
    setTickets(updatedTickets);
    setShowDeleteConfirm(false);
    setTicketToDelete('');
    // If the deleted ticket was selected, clear selection
    if (selectedTicket === ticketToDelete) {
      setSelectedTicket(null);
    }
  };
  // Refresh tickets
  const refreshTickets = () => {
    setIsLoading(true);
    // Simulate API call delay
    setTimeout(() => {
      let allTickets = [];
      // If super admin, load all tickets
      // If MDA admin, only load tickets for their MDA
      if (isSuperAdmin()) {
        allTickets = generateMockTickets(50);
      } else {
        const mdaName = getMdaName();
        allTickets = generateMockTickets(20, mdaName);
      }
      setTickets(allTickets);
      setFilteredTickets(allTickets);
      // Calculate ticket stats
      const openTickets = allTickets.filter((t: any) => t.status === 'Open').length;
      const inProgressTickets = allTickets.filter((t: any) => t.status === 'In Progress').length;
      const resolvedTickets = allTickets.filter((t: any) => t.status === 'Resolved').length;
      setTicketStats({
        total: allTickets.length,
        open: openTickets,
        inProgress: inProgressTickets,
        resolved: resolvedTickets,
        averageResponseTime: '4.2h',
        resolutionRate: '87%'
      });
      setIsLoading(false);
      setSelectedTicket(null);
    }, 1000);
  };
  // Get priority badge class
  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case 'Low':
        return 'bg-blue-100 text-blue-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'High':
        return 'bg-orange-100 text-orange-800';
      case 'Urgent':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status badge class
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Open':
        return 'bg-green-100 text-green-800';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800';
      case 'Resolved':
        return 'bg-purple-100 text-purple-800';
      case 'Closed':
        return 'bg-gray-100 text-gray-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get category badge
  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'Technical':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800" data-id="element-3919">
            <HelpCircleIcon size={12} className="mr-1" data-id="element-3920" />
            Technical
          </span>;
      case 'Account':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-3921">
            <UserIcon size={12} className="mr-1" data-id="element-3922" />
            Account
          </span>;
      case 'Payment':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-3923">
            <TagIcon size={12} className="mr-1" data-id="element-3924" />
            Payment
          </span>;
      case 'Application':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800" data-id="element-3925">
            <FileTextIcon size={12} className="mr-1" data-id="element-3926" />
            Application
          </span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-3927">
            <MessageSquareIcon size={12} className="mr-1" data-id="element-3928" />
            General
          </span>;
    }
  };
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100" data-id="element-3929">
        <div className="flex flex-col items-center" data-id="element-3930">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4" data-id="element-3931"></div>
          <p className="text-gray-600" data-id="element-3932">Loading support tickets...</p>
        </div>
      </div>;
  }
  return <div className="flex h-screen bg-gray-100" data-id="element-3933">
      {/* Sidebar - hidden on mobile unless toggled */}
      <div className={`${isSidebarOpen ? 'block' : 'hidden'} lg:block fixed inset-y-0 left-0 z-50 lg:relative lg:z-0`} data-id="element-3934">
        <AdminSidebar data-id="element-3935" />
      </div>
      {/* Backdrop for mobile sidebar */}
      {isSidebarOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-3936"></div>}
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-3937">
        <AdminHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} data-id="element-3938" />
        <main className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-3939">
          {/* Page Header */}
          <div className="mb-6" data-id="element-3940">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-3941">
              Support
              {!isSuperAdmin() && <span className="ml-2 text-lg font-normal text-gray-600" data-id="element-3942">
                  ({getMdaName()})
                </span>}
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-3943">
              {isSuperAdmin() ? 'Manage and respond to support tickets from all MDAs' : `Manage and respond to support tickets for ${getMdaName()}`}
            </p>
          </div>
          {/* Support Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-3944">
            <div className="bg-white rounded-lg shadow p-4" data-id="element-3945">
              <div className="flex justify-between items-start" data-id="element-3946">
                <div data-id="element-3947">
                  <p className="text-sm font-medium text-gray-500" data-id="element-3948">
                    Open Tickets
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-3949">
                    {ticketStats.open}
                  </p>
                </div>
                <div className="p-2 bg-green-100 rounded-md" data-id="element-3950">
                  <MessageCircleIcon size={20} className="text-green-600" data-id="element-3951" />
                </div>
              </div>
              <div className="mt-2 flex items-center text-xs text-gray-600" data-id="element-3952">
                <span data-id="element-3953">
                  {Math.round(ticketStats.open / ticketStats.total * 100)}% of
                  total tickets
                </span>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4" data-id="element-3954">
              <div className="flex justify-between items-start" data-id="element-3955">
                <div data-id="element-3956">
                  <p className="text-sm font-medium text-gray-500" data-id="element-3957">
                    In Progress
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-3958">
                    {ticketStats.inProgress}
                  </p>
                </div>
                <div className="p-2 bg-blue-100 rounded-md" data-id="element-3959">
                  <ClockIcon size={20} className="text-blue-600" data-id="element-3960" />
                </div>
              </div>
              <div className="mt-2 flex items-center text-xs text-gray-600" data-id="element-3961">
                <span data-id="element-3962">
                  {Math.round(ticketStats.inProgress / ticketStats.total * 100)}
                  % of total tickets
                </span>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-4" data-id="element-3963">
              <div className="flex justify-between items-start" data-id="element-3964">
                <div data-id="element-3965">
                  <p className="text-sm font-medium text-gray-500" data-id="element-3966">
                    Avg. Response Time
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-3967">
                    {ticketStats.averageResponseTime}
                  </p>
                </div>
                <div className="p-2 bg-purple-100 rounded-md" data-id="element-3968">
                  <CheckCircleIcon size={20} className="text-purple-600" data-id="element-3969" />
                </div>
              </div>
              <div className="mt-2 flex items-center text-xs text-green-600" data-id="element-3970">
                <ArrowDownIcon size={14} className="mr-1" data-id="element-3971" />
                <span data-id="element-3972">12% improvement</span>
              </div>
            </div>
          </div>
          {/* Support Controls */}
          <div className="bg-white rounded-lg shadow mb-6" data-id="element-3973">
            <div className="p-4 border-b border-gray-200" data-id="element-3974">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-3975">
                {/* Search */}
                <div className="relative flex-1" data-id="element-3976">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3977">
                    <SearchIcon size={18} className="text-gray-400" data-id="element-3978" />
                  </div>
                  <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm" placeholder="Search tickets by subject, user, or description" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3979" />
                </div>
                {/* Filters */}
                <div className="flex flex-wrap gap-2" data-id="element-3980">
                  <div className="relative" data-id="element-3981">
                    <button className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" onClick={() => toggleDropdown('filter')} data-id="element-3982">
                      <FilterIcon size={18} className="mr-2" data-id="element-3983" />
                      Filters
                      <ChevronDownIcon size={18} className="ml-2" data-id="element-3984" />
                    </button>
                    {activeDropdown === 'filter' && <div className="origin-top-right absolute right-0 mt-2 w-72 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-3985">
                        <div className="py-1 p-3" data-id="element-3986">
                          <div className="mb-3" data-id="element-3987">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3988">
                              Status
                            </label>
                            <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-3989">
                              <option value="all" data-id="element-3990">All Statuses</option>
                              <option value="Open" data-id="element-3991">Open</option>
                              <option value="In Progress" data-id="element-3992">In Progress</option>
                              <option value="Resolved" data-id="element-3993">Resolved</option>
                              <option value="Closed" data-id="element-3994">Closed</option>
                              <option value="Pending" data-id="element-3995">Pending</option>
                            </select>
                          </div>
                          <div className="mb-3" data-id="element-3996">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3997">
                              Priority
                            </label>
                            <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md" value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} data-id="element-3998">
                              <option value="all" data-id="element-3999">All Priorities</option>
                              <option value="Low" data-id="element-4000">Low</option>
                              <option value="Medium" data-id="element-4001">Medium</option>
                              <option value="High" data-id="element-4002">High</option>
                              <option value="Urgent" data-id="element-4003">Urgent</option>
                            </select>
                          </div>
                          <div className="mb-3" data-id="element-4004">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4005">
                              Category
                            </label>
                            <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} data-id="element-4006">
                              <option value="all" data-id="element-4007">All Categories</option>
                              <option value="Technical" data-id="element-4008">Technical</option>
                              <option value="Account" data-id="element-4009">Account</option>
                              <option value="Payment" data-id="element-4010">Payment</option>
                              <option value="Application" data-id="element-4011">Application</option>
                              <option value="General" data-id="element-4012">General</option>
                            </select>
                          </div>
                          <div className="mb-3" data-id="element-4013">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4014">
                              Sort Order
                            </label>
                            <select className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md" value={sortOrder} onChange={e => setSortOrder(e.target.value)} data-id="element-4015">
                              <option value="newest" data-id="element-4016">Newest First</option>
                              <option value="oldest" data-id="element-4017">Oldest First</option>
                            </select>
                          </div>
                          <div className="flex justify-between pt-2" data-id="element-4018">
                            <button className="text-sm text-gray-700 hover:text-gray-900" onClick={() => {
                          setStatusFilter('all');
                          setPriorityFilter('all');
                          setCategoryFilter('all');
                          setSortOrder('newest');
                        }} data-id="element-4019">
                              Reset Filters
                            </button>
                            <button className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700" onClick={() => setActiveDropdown(null)} data-id="element-4020">
                              Apply
                            </button>
                          </div>
                        </div>
                      </div>}
                  </div>
                  <button className={`px-4 py-2 rounded-md text-sm font-medium ${activeFilter === 'all' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} onClick={() => setActiveFilter('all')} data-id="element-4021">
                    All
                  </button>
                  <button className={`px-4 py-2 rounded-md text-sm font-medium ${activeFilter === 'unassigned' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} onClick={() => setActiveFilter('unassigned')} data-id="element-4022">
                    Unassigned
                  </button>
                  <button className={`px-4 py-2 rounded-md text-sm font-medium ${activeFilter === 'urgent' ? 'bg-green-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} onClick={() => setActiveFilter('urgent')} data-id="element-4023">
                    Urgent
                  </button>
                  <button className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" onClick={refreshTickets} disabled={isLoading} data-id="element-4024">
                    <RefreshCwIcon size={18} className={`mr-2 ${isLoading ? 'animate-spin' : ''}`} data-id="element-4025" />
                    Refresh
                  </button>
                </div>
              </div>
            </div>
            {/* Create Ticket Button */}
            {canManageTickets && <div className="p-4 bg-gray-50 flex justify-end" data-id="element-4026">
                <button onClick={() => setShowNewTicketModal(true)} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-4027">
                  <PlusIcon size={18} className="mr-2" data-id="element-4028" />
                  Create Ticket
                </button>
              </div>}
          </div>
          {/* Support Content */}
          <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-4029">
            {filteredTickets.length === 0 ? <div className="p-8 text-center" data-id="element-4030">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100" data-id="element-4031">
                  <MessageCircleIcon size={24} className="text-gray-400" data-id="element-4032" />
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-4033">
                  No tickets found
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-4034">
                  {searchQuery ? 'Try adjusting your search or filter criteria.' : 'There are no support tickets matching your current filters.'}
                </p>
                <div className="mt-6" data-id="element-4035">
                  <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" onClick={refreshTickets} data-id="element-4036">
                    <RefreshCwIcon size={16} className="mr-2" data-id="element-4037" />
                    Refresh
                  </button>
                </div>
              </div> : <div className="flex flex-col md:flex-row h-full" data-id="element-4038">
                {/* Ticket List */}
                <div className={`w-full md:w-1/3 lg:w-1/4 border-r border-gray-200 ${selectedTicket ? 'hidden md:block' : ''}`} data-id="element-4039">
                  <div className="overflow-y-auto h-[calc(100vh-20rem)]" data-id="element-4040">
                    {filteredTickets.map(ticket => <div key={ticket.id} className={`border-b border-gray-200 p-4 cursor-pointer hover:bg-gray-50 ${selectedTicket === ticket.id ? 'bg-green-50 border-l-4 border-l-green-500' : ''}`} onClick={() => selectTicket(ticket.id)} data-id="element-4041">
                        <div className="flex justify-between items-start mb-2" data-id="element-4042">
                          <h3 className="text-sm font-medium text-gray-900 line-clamp-1" data-id="element-4043">
                            {ticket.subject}
                          </h3>
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadgeClass(ticket.status)}`} data-id="element-4044">
                            {ticket.status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mb-2 line-clamp-2" data-id="element-4045">
                          {ticket.description}
                        </p>
                        <div className="flex justify-between items-center text-xs" data-id="element-4046">
                          <div className="flex items-center" data-id="element-4047">
                            <UserIcon size={12} className="text-gray-400 mr-1" data-id="element-4048" />
                            <span className="text-gray-600" data-id="element-4049">
                              {ticket.user.name}
                            </span>
                          </div>
                          <div className="flex items-center" data-id="element-4050">
                            <ClockIcon size={12} className="text-gray-400 mr-1" data-id="element-4051" />
                            <span className="text-gray-600" data-id="element-4052">
                              {formatRelativeTime(ticket.date)}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2" data-id="element-4053">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityBadgeClass(ticket.priority)}`} data-id="element-4054">
                            {ticket.priority}
                          </span>
                          {isSuperAdmin() && <span className="text-xs text-gray-500" data-id="element-4055">
                              {ticket.mda}
                            </span>}
                        </div>
                      </div>)}
                  </div>
                </div>
                {/* Ticket Detail */}
                {selectedTicket ? <div className="w-full md:w-2/3 lg:w-3/4 flex flex-col" data-id="element-4056">
                    {tickets.filter(t => t.id === selectedTicket).map(ticket => <div key={ticket.id} className="flex flex-col h-full" data-id="element-4057">
                          {/* Ticket Header */}
                          <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-4058">
                            <div className="flex items-center justify-between mb-2" data-id="element-4059">
                              <button className="md:hidden flex items-center text-gray-500 hover:text-gray-700" onClick={() => setSelectedTicket(null)} data-id="element-4060">
                                <ArrowLeftIcon size={16} className="mr-1" data-id="element-4061" />
                                Back
                              </button>
                              <div className="flex space-x-2" data-id="element-4062">
                                {canAssignTickets && <button onClick={() => {
                        setSelectedTicketId(ticket.id);
                        setShowAssignModal(true);
                      }} className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-md hover:bg-blue-100" data-id="element-4063">
                                    {ticket.assignedTo ? 'Reassign' : 'Assign'}
                                  </button>}
                                {canDeleteTickets && <button onClick={() => deleteTicket(ticket.id)} className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded-md hover:bg-red-100" data-id="element-4064">
                                    Delete
                                  </button>}
                                <div className="relative" data-id="element-4065">
                                  <button onClick={() => toggleDropdown('ticketActions')} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md hover:bg-gray-200" data-id="element-4066">
                                    <MoreHorizontalIcon size={14} data-id="element-4067" />
                                  </button>
                                  {activeDropdown === 'ticketActions' && <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10" data-id="element-4068">
                                      <div className="py-1" data-id="element-4069">
                                        <button className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100" onClick={() => {
                              toggleDropdown('ticketActions');
                              // Print functionality would go here
                            }} data-id="element-4070">
                                          Print Ticket
                                        </button>
                                        <button className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100" onClick={() => {
                              toggleDropdown('ticketActions');
                              // Export functionality would go here
                            }} data-id="element-4071">
                                          Export as PDF
                                        </button>
                                        <button className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100" onClick={() => {
                              toggleDropdown('ticketActions');
                              // Share functionality would go here
                            }} data-id="element-4072">
                                          Share Ticket
                                        </button>
                                      </div>
                                    </div>}
                                </div>
                              </div>
                            </div>
                            <h2 className="text-lg font-medium text-gray-900" data-id="element-4073">
                              {ticket.subject}
                            </h2>
                            <div className="flex flex-wrap items-center gap-2 mt-2" data-id="element-4074">
                              <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadgeClass(ticket.status)}`} data-id="element-4075">
                                {ticket.status}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded-full ${getPriorityBadgeClass(ticket.priority)}`} data-id="element-4076">
                                {ticket.priority}
                              </span>
                              {getCategoryBadge(ticket.category)}
                              {isSuperAdmin() && <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full" data-id="element-4077">
                                  {ticket.mda}
                                </span>}
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-4 text-sm" data-id="element-4078">
                              <div data-id="element-4079">
                                <p className="text-gray-500" data-id="element-4080">Submitted by</p>
                                <div className="flex items-center mt-1" data-id="element-4081">
                                  <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs mr-2" data-id="element-4082">
                                    {ticket.user.name.charAt(0)}
                                  </div>
                                  <div data-id="element-4083">
                                    <p className="font-medium text-gray-900" data-id="element-4084">
                                      {ticket.user.name}
                                    </p>
                                    <div className="flex items-center text-gray-500 text-xs" data-id="element-4085">
                                      <MailIcon size={10} className="mr-1" data-id="element-4086" />
                                      {ticket.user.email}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div data-id="element-4087">
                                <p className="text-gray-500" data-id="element-4088">Assigned to</p>
                                {ticket.assignedTo ? <div className="flex items-center mt-1" data-id="element-4089">
                                    <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 text-xs mr-2" data-id="element-4090">
                                      {ticket.assignedTo.name.charAt(0)}
                                    </div>
                                    <p className="font-medium text-gray-900" data-id="element-4091">
                                      {ticket.assignedTo.name}
                                    </p>
                                  </div> : <p className="text-gray-400 mt-1" data-id="element-4092">
                                    Unassigned
                                  </p>}
                              </div>
                              <div data-id="element-4093">
                                <p className="text-gray-500" data-id="element-4094">Created</p>
                                <p className="font-medium text-gray-900" data-id="element-4095">
                                  {new Date(ticket.date).toLocaleDateString()}{' '}
                                  at{' '}
                                  {new Date(ticket.date).toLocaleTimeString()}
                                </p>
                              </div>
                              <div data-id="element-4096">
                                <p className="text-gray-500" data-id="element-4097">Last Updated</p>
                                <p className="font-medium text-gray-900" data-id="element-4098">
                                  {formatRelativeTime(ticket.lastUpdated)}
                                </p>
                              </div>
                            </div>
                          </div>
                          {/* Ticket Messages */}
                          <div className="flex-1 overflow-y-auto p-4 bg-gray-50" data-id="element-4099">
                            <div className="space-y-4" data-id="element-4100">
                              {ticket.messages.map((message, index) => <div key={message.id} className={`flex ${message.isUser ? 'justify-start' : 'justify-end'}`} data-id="element-4101">
                                  <div className={`max-w-[75%] rounded-lg p-3 ${message.isUser ? 'bg-white border border-gray-200' : 'bg-green-50 border border-green-200'}`} data-id="element-4102">
                                    <div className="flex items-center mb-1" data-id="element-4103">
                                      <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs mr-2 ${message.isUser ? 'bg-gray-200 text-gray-600' : 'bg-green-200 text-green-600'}`} data-id="element-4104">
                                        {message.isUser ? 'U' : 'S'}
                                      </div>
                                      <span className="text-xs font-medium" data-id="element-4105">
                                        {message.sender}
                                      </span>
                                      <span className="text-xs text-gray-500 ml-auto" data-id="element-4106">
                                        {formatRelativeTime(message.date)}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-900" data-id="element-4107">
                                      {message.content}
                                    </p>
                                  </div>
                                </div>)}
                              <div ref={messageEndRef} data-id="element-4108" />
                            </div>
                          </div>
                          {/* Status Update */}
                          {canManageTickets && <div className="p-3 border-t border-gray-200 bg-gray-50" data-id="element-4109">
                              <div className="flex items-center justify-between" data-id="element-4110">
                                <p className="text-sm font-medium text-gray-700" data-id="element-4111">
                                  Update Status:
                                </p>
                                <div className="flex space-x-2" data-id="element-4112">
                                  {ticket.status !== 'Open' && <button onClick={() => updateTicketStatus(ticket.id, 'Open')} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-md hover:bg-green-200" data-id="element-4113">
                                      Open
                                    </button>}
                                  {ticket.status !== 'In Progress' && <button onClick={() => updateTicketStatus(ticket.id, 'In Progress')} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-md hover:bg-blue-200" data-id="element-4114">
                                      In Progress
                                    </button>}
                                  {ticket.status !== 'Resolved' && <button onClick={() => updateTicketStatus(ticket.id, 'Resolved')} className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-md hover:bg-purple-200" data-id="element-4115">
                                      Resolved
                                    </button>}
                                  {ticket.status !== 'Closed' && <button onClick={() => updateTicketStatus(ticket.id, 'Closed')} className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-md hover:bg-gray-200" data-id="element-4116">
                                      Closed
                                    </button>}
                                  {ticket.status !== 'Pending' && <button onClick={() => updateTicketStatus(ticket.id, 'Pending')} className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-md hover:bg-yellow-200" data-id="element-4117">
                                      Pending
                                    </button>}
                                </div>
                              </div>
                            </div>}
                          {/* Reply Box */}
                          {ticket.status !== 'Closed' && <div className="p-4 border-t border-gray-200" data-id="element-4118">
                              <div className="flex items-start" data-id="element-4119">
                                <div className="flex-1" data-id="element-4120">
                                  <textarea className="w-full border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 p-2" rows={3} placeholder="Type your reply here..." value={newMessage} onChange={e => setNewMessage(e.target.value)} data-id="element-4121"></textarea>
                                  <div className="mt-2 flex justify-between items-center" data-id="element-4122">
                                    <div className="flex space-x-2" data-id="element-4123">
                                      <button className="text-gray-500 hover:text-gray-700" data-id="element-4124">
                                        <PaperclipIcon size={18} data-id="element-4125" />
                                      </button>
                                      <button className="text-gray-500 hover:text-gray-700" data-id="element-4126">
                                        <SmileIcon size={18} data-id="element-4127" />
                                      </button>
                                      <button className="text-gray-500 hover:text-gray-700" data-id="element-4128">
                                        <ImageIcon size={18} data-id="element-4129" />
                                      </button>
                                    </div>
                                    <button onClick={sendMessage} disabled={newMessage.trim() === '' || isReplying} className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${newMessage.trim() === '' || isReplying ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`} data-id="element-4130">
                                      {isReplying ? <>
                                          <RefreshCwIcon size={16} className="mr-2 animate-spin" data-id="element-4131" />
                                          Sending...
                                        </> : <>
                                          <SendIcon size={16} className="mr-2" data-id="element-4132" />
                                          Send Reply
                                        </>}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>}
                        </div>)}
                  </div> : <div className="w-full md:w-2/3 lg:w-3/4 flex items-center justify-center p-8 bg-gray-50" data-id="element-4133">
                    <div className="text-center" data-id="element-4134">
                      <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100" data-id="element-4135">
                        <MessageCircleIcon size={24} className="text-gray-400" data-id="element-4136" />
                      </div>
                      <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-4137">
                        No ticket selected
                      </h3>
                      <p className="mt-1 text-sm text-gray-500" data-id="element-4138">
                        Select a ticket from the list to view details and
                        respond.
                      </p>
                    </div>
                  </div>}
              </div>}
          </div>
          {/* Knowledge Base Quick Links */}
          <div className="bg-white rounded-lg shadow p-4 mt-6" data-id="element-4139">
            <div className="flex justify-between items-center mb-4" data-id="element-4140">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-4141">
                Support Resources
              </h3>
              <button className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md hover:bg-gray-200" data-id="element-4142">
                <MoreHorizontalIcon size={14} data-id="element-4143" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4144">
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-4145">
                <div className="flex items-start" data-id="element-4146">
                  <div className="p-2 bg-blue-100 rounded-md mr-3" data-id="element-4147">
                    <HelpCircleIcon size={20} className="text-blue-600" data-id="element-4148" />
                  </div>
                  <div data-id="element-4149">
                    <h4 className="text-sm font-medium text-gray-900 mb-1" data-id="element-4150">
                      Knowledge Base
                    </h4>
                    <p className="text-xs text-gray-600 mb-2" data-id="element-4151">
                      Access articles and guides to help resolve common issues.
                    </p>
                    <a href="#" className="text-xs text-blue-600 hover:text-blue-800 flex items-center" data-id="element-4152">
                      Browse Articles
                      <ArrowRightIcon size={14} className="ml-1" data-id="element-4153" />
                    </a>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-4154">
                <div className="flex items-start" data-id="element-4155">
                  <div className="p-2 bg-purple-100 rounded-md mr-3" data-id="element-4156">
                    <FileTextIcon size={20} className="text-purple-600" data-id="element-4157" />
                  </div>
                  <div data-id="element-4158">
                    <h4 className="text-sm font-medium text-gray-900 mb-1" data-id="element-4159">
                      Response Templates
                    </h4>
                    <p className="text-xs text-gray-600 mb-2" data-id="element-4160">
                      Use pre-written templates for common support scenarios.
                    </p>
                    <a href="#" className="text-xs text-purple-600 hover:text-purple-800 flex items-center" data-id="element-4161">
                      View Templates
                      <ArrowRightIcon size={14} className="ml-1" data-id="element-4162" />
                    </a>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-4163">
                <div className="flex items-start" data-id="element-4164">
                  <div className="p-2 bg-green-100 rounded-md mr-3" data-id="element-4165">
                    <PhoneIcon size={20} className="text-green-600" data-id="element-4166" />
                  </div>
                  <div data-id="element-4167">
                    <h4 className="text-sm font-medium text-gray-900 mb-1" data-id="element-4168">
                      Escalation Procedures
                    </h4>
                    <p className="text-xs text-gray-600 mb-2" data-id="element-4169">
                      Learn when and how to escalate support tickets.
                    </p>
                    <a href="#" className="text-xs text-green-600 hover:text-green-800 flex items-center" data-id="element-4170">
                      View Guidelines
                      <ArrowRightIcon size={14} className="ml-1" data-id="element-4171" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      {/* New Ticket Modal */}
      {showNewTicketModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-4172">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full" data-id="element-4173">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-4174">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-4175">
                Create New Support Ticket
              </h3>
            </div>
            <div className="p-6" data-id="element-4176">
              <div className="mb-4" data-id="element-4177">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4178">
                  Subject
                </label>
                <input type="text" className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" placeholder="Enter ticket subject" value={newTicket.subject} onChange={e => setNewTicket({
              ...newTicket,
              subject: e.target.value
            })} data-id="element-4179" />
              </div>
              <div className="mb-4" data-id="element-4180">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4181">
                  Description
                </label>
                <textarea className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" rows={4} placeholder="Enter ticket description" value={newTicket.description} onChange={e => setNewTicket({
              ...newTicket,
              description: e.target.value
            })} data-id="element-4182"></textarea>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4183">
                <div data-id="element-4184">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4185">
                    Category
                  </label>
                  <select className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" value={newTicket.category} onChange={e => setNewTicket({
                ...newTicket,
                category: e.target.value
              })} data-id="element-4186">
                    <option value="Technical" data-id="element-4187">Technical</option>
                    <option value="Account" data-id="element-4188">Account</option>
                    <option value="Payment" data-id="element-4189">Payment</option>
                    <option value="Application" data-id="element-4190">Application</option>
                    <option value="General" data-id="element-4191">General</option>
                  </select>
                </div>
                <div data-id="element-4192">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4193">
                    Priority
                  </label>
                  <select className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" value={newTicket.priority} onChange={e => setNewTicket({
                ...newTicket,
                priority: e.target.value
              })} data-id="element-4194">
                    <option value="Low" data-id="element-4195">Low</option>
                    <option value="Medium" data-id="element-4196">Medium</option>
                    <option value="High" data-id="element-4197">High</option>
                    <option value="Urgent" data-id="element-4198">Urgent</option>
                  </select>
                </div>
              </div>
              {isSuperAdmin() && <div className="mb-4" data-id="element-4199">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4200">
                    MDA
                  </label>
                  <select className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" value={newTicket.mda} onChange={e => setNewTicket({
              ...newTicket,
              mda: e.target.value
            })} data-id="element-4201">
                    <option value="" data-id="element-4202">Select MDA</option>
                    <option value="Ministry of Finance" data-id="element-4203">
                      Ministry of Finance
                    </option>
                    <option value="Ministry of Lands and Surveys" data-id="element-4204">
                      Ministry of Lands and Surveys
                    </option>
                    <option value="Ministry of Commerce and Industry" data-id="element-4205">
                      Ministry of Commerce and Industry
                    </option>
                    <option value="Federal Road Safety Commission" data-id="element-4206">
                      Federal Road Safety Commission
                    </option>
                    <option value="Ministry of Education" data-id="element-4207">
                      Ministry of Education
                    </option>
                  </select>
                </div>}
              <div className="mb-4" data-id="element-4208">
                <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-4209">
                  User Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4210">
                  <div data-id="element-4211">
                    <label className="block text-xs text-gray-500 mb-1" data-id="element-4212">
                      Name
                    </label>
                    <input type="text" className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500" placeholder="Enter user name" value={newTicket.user.name} onChange={e => setNewTicket({
                  ...newTicket,
                  user: {
                    ...newTicket.user,
                    name: e.target.value
                  }
                })} data-id="element-4213" />
                  </div>
                  <div data-id="element-4214">
                    <label className="block text-xs text-gray-500 mb-1" data-id="element-4215">
                      Email
                    </label>
                    <input type="email" className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500" placeholder="Enter user email" value={newTicket.user.email} onChange={e => setNewTicket({
                  ...newTicket,
                  user: {
                    ...newTicket.user,
                    email: e.target.value
                  }
                })} data-id="element-4216" />
                  </div>
                  <div className="md:col-span-2" data-id="element-4217">
                    <label className="block text-xs text-gray-500 mb-1" data-id="element-4218">
                      Phone (optional)
                    </label>
                    <input type="text" className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 text-sm focus:ring-1 focus:ring-green-500 focus:border-green-500" placeholder="Enter user phone" value={newTicket.user.phone} onChange={e => setNewTicket({
                  ...newTicket,
                  user: {
                    ...newTicket.user,
                    phone: e.target.value
                  }
                })} data-id="element-4219" />
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6" data-id="element-4220">
                <button onClick={() => setShowNewTicketModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-4221">
                  Cancel
                </button>
                <button onClick={createTicket} disabled={newTicket.subject.trim() === '' || newTicket.description.trim() === '' || newTicket.user.name.trim() === '' || newTicket.user.email.trim() === '' || isSuperAdmin() && newTicket.mda === '' || isSubmittingTicket} className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${newTicket.subject.trim() === '' || newTicket.description.trim() === '' || newTicket.user.name.trim() === '' || newTicket.user.email.trim() === '' || isSuperAdmin() && newTicket.mda === '' || isSubmittingTicket ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`} data-id="element-4222">
                  {isSubmittingTicket ? <>
                      <RefreshCwIcon size={16} className="inline mr-2 animate-spin" data-id="element-4223" />
                      Creating...
                    </> : 'Create Ticket'}
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Assign Ticket Modal */}
      {showAssignModal && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-4224">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-4225">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-4226">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-4227">
                Assign Ticket
              </h3>
            </div>
            <div className="p-6" data-id="element-4228">
              <div className="mb-4" data-id="element-4229">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4230">
                  Assign to
                </label>
                <select className="block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:ring-1 focus:ring-green-500 focus:border-green-500" value={selectedAssignee} onChange={e => setSelectedAssignee(e.target.value)} data-id="element-4231">
                  <option value="" data-id="element-4232">Select Support Agent</option>
                  <option value="John Smith" data-id="element-4233">John Smith</option>
                  <option value="Sarah Johnson" data-id="element-4234">Sarah Johnson</option>
                  <option value="Michael Brown" data-id="element-4235">Michael Brown</option>
                  <option value="Emily Davis" data-id="element-4236">Emily Davis</option>
                  <option value="Robert Wilson" data-id="element-4237">Robert Wilson</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3 mt-6" data-id="element-4238">
                <button onClick={() => setShowAssignModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-4239">
                  Cancel
                </button>
                <button onClick={assignTicket} disabled={selectedAssignee.trim() === '' || isAssigning} className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${selectedAssignee.trim() === '' || isAssigning ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`} data-id="element-4240">
                  {isAssigning ? <>
                      <RefreshCwIcon size={16} className="inline mr-2 animate-spin" data-id="element-4241" />
                      Assigning...
                    </> : 'Assign Ticket'}
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-4242">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-4243">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-4244">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-4245">
                Confirm Delete
              </h3>
            </div>
            <div className="p-6" data-id="element-4246">
              <div className="flex items-center mb-4" data-id="element-4247">
                <div className="flex-shrink-0 bg-red-100 rounded-full p-2" data-id="element-4248">
                  <AlertCircleIcon size={24} className="text-red-600" data-id="element-4249" />
                </div>
                <div className="ml-3" data-id="element-4250">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-4251">
                    Are you sure you want to delete this ticket?
                  </h3>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-4252">
                    This action cannot be undone. All ticket data will be
                    permanently removed.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end space-x-3" data-id="element-4253">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-4254">
                Cancel
              </button>
              <button onClick={confirmDeleteTicket} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700" data-id="element-4255">
                Delete
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
export default Support;